import { OrgStatus, SettlementMode } from '@constants/generic';
import { gstinRegex, hsnCodeRegex } from '@constants/regex';
import { GstInput, GstOptions } from '@features/settings/types';
import { BranchForm } from '@types';
import { isInvalidEntityName } from '@utils/validations';
import { BankAccount } from 'types/account-detail';

import { BranchByIdType } from './constants';
import { FeeListType } from './types';

export const getTotalAccountsInBranch = (accountsData: any) => {
  const uniqueAccountsData = [
    accountsData.branch?.bank_account
      ? {
          ...accountsData.branch.bank_account,
          accountDetailId: accountsData.branch.id,
          is_primary: accountsData.branch.is_primary
        }
      : null,
    accountsData.organisation?.bank_account
      ? {
          ...accountsData.organisation.bank_account,
          accountDetailId: accountsData.organisation.id,
          is_primary: accountsData.organisation.is_primary
        }
      : null,
    ...(accountsData.secondary_accounts?.length
      ? accountsData.secondary_accounts.map(
          (account: { bank_account: any; id: any; is_primary: any }) => ({
            ...account.bank_account,
            accountDetailId: account.id,
            is_primary: account.is_primary
          })
        )
      : [])
  ];
  return Array.from(uniqueAccountsData).length;
};

export const getUniqueAccountsDataForFeeCategory = (
  accountsData: any
): BankAccount[] => {
  const uniqueAccountsData = [
    accountsData?.branch?.bank_account
      ? {
          ...accountsData?.branch?.bank_account,
          accountDetailId: accountsData?.branch?.id,
          is_primary: accountsData?.branch?.is_primary
        }
      : null,
    accountsData?.organisation?.bank_account
      ? {
          ...accountsData?.organisation?.bank_account,
          accountDetailId: accountsData?.organisation?.id,
          is_primary: accountsData?.organisation?.is_primary
        }
      : null,
    ...(accountsData?.secondary_accounts?.length
      ? accountsData.secondary_accounts.map(
          (account: { bank_account: any; id: any; is_primary: any }) => ({
            ...account.bank_account,
            accountDetailId: account.id,
            is_primary: account.is_primary
          })
        )
      : [])
  ]
    .filter(Boolean)
    .reduce((acc, account) => {
      if (account && !acc.has(account.accountDetailId)) {
        acc.set(account.accountDetailId, account);
      }
      return acc;
    }, new Map<string, BankAccount>())
    .values();
  return Array.from(uniqueAccountsData);
};

/** Backend may omit or null `gst_type`; use `is_inclusive_gst` when absent. */
export const getBranchEffectiveGstType = (
  branchDetails: Pick<BranchByIdType, 'gst_type' | 'is_inclusive_gst'>
): GstOptions => {
  const raw = branchDetails?.gst_type?.trim();
  if (
    raw === GstOptions.EXCLUSIVE ||
    raw === GstOptions.INCLUSIVE ||
    raw === GstOptions.COMPOSITION
  ) {
    return raw;
  }
  if (branchDetails?.is_inclusive_gst) return GstOptions.INCLUSIVE;
  return GstOptions.EXCLUSIVE;
};

export const checkForChangeInGstFeature = (
  branchDetails: BranchByIdType,
  gstInfo: GstInput,
  formData: BranchForm
) => {
  const branchCgst = branchDetails?.cgst?.trim();
  const branchSgst = branchDetails?.sgst?.trim();
  const formCgst = gstInfo?.gstValue.cgst?.trim() ?? '';
  const formSgst = gstInfo?.gstValue.sgst?.trim() ?? '';
  const isChangeInGstFeature =
    branchDetails?.activate_gst_feature !== gstInfo.showGstDetails ||
    branchDetails?.gstin?.trim() !== gstInfo?.gstIn.trim() ||
    branchDetails?.hsn_code?.trim() !== gstInfo?.hsnCode?.trim() ||
    (Boolean(branchCgst) && Number(branchCgst) !== Number(formCgst || 0)) ||
    (Boolean(branchSgst) && Number(branchSgst) !== Number(formSgst || 0)) ||
    (formData.country &&
      formData.state &&
      gstInfo?.gstValue.igst &&
      Boolean(branchDetails?.igst?.trim()) &&
      Number(branchDetails?.igst?.trim()) !==
        Number(String(gstInfo?.gstValue.igst).trim()));

  return isChangeInGstFeature;
};

export const checkForChangeInBranchDetails = (
  branchDetails: BranchByIdType,
  formData: BranchForm,
  hasChangeInSettlementMode: boolean,
  isFeeCategoryEnabled: boolean,
  hasChangeInFeeCategory: boolean,
  gstInfo: GstInput,
  hasChangeInGstFeature: boolean
) => {
  const isAddressChange =
    branchDetails.address_line_1 !== formData?.addressLine1 ||
    branchDetails.address_line_2 !== formData?.addressLine2 ||
    branchDetails.city !== formData?.city ||
    branchDetails.state !== formData?.state ||
    branchDetails.country !== formData?.country ||
    branchDetails.pincode !== formData?.pincode;

  const isChangeInFineOrPartialPaymentConfig =
    branchDetails?.branch_config?.fine_config?.fine_amount?.toString() !==
      formData?.branchConfig?.fine_config?.fine_amount?.toString() ||
    branchDetails?.branch_config?.fine_config?.fine_type !==
      formData?.branchConfig?.fine_config?.fine_type ||
    branchDetails?.branch_config?.fine_config?.fine_starts_in_days !==
      formData?.branchConfig?.fine_config?.fine_starts_in_days ||
    branchDetails?.branch_config?.limits_config?.split_payment_limits
      ?.per_split_amount_automatic_min !==
      formData?.branchConfig?.limits_config?.split_payment_limits
        ?.per_split_amount_automatic_min;

  const isChangeInBranchDetails =
    branchDetails.name !== formData?.branchName ||
    branchDetails?.image_url !== formData?.branchImage?.image_url ||
    (!branchDetails?.image_url && formData?.branchImage?.file) ||
    isAddressChange ||
    hasChangeInSettlementMode ||
    branchDetails.is_fee_configured !== isFeeCategoryEnabled ||
    (isFeeCategoryEnabled && hasChangeInFeeCategory) ||
    (gstInfo.showGstDetails &&
      gstInfo.gstIn.trim().length === 0 &&
      gstInfo.hsnCode.trim().length === 0 &&
      Number(gstInfo.gstValue.cgst) > 0 &&
      Number(gstInfo.gstValue.sgst) > 0 &&
      gstInfo.gstValue.igst == null) ||
    hasChangeInGstFeature ||
    (gstInfo.showGstDetails &&
      getBranchEffectiveGstType(branchDetails) !== gstInfo.selectedGstType) ||
    isChangeInFineOrPartialPaymentConfig;

  return isChangeInBranchDetails;
};

export const checkForChangeInSettlementMode = (
  primaryAccountDetails: any,
  preferredAccount: BankAccount
) =>
  primaryAccountDetails?.branch?.bank_account
    ? primaryAccountDetails?.branch?.bank_account?.bank_account_number !==
        preferredAccount.bank_account_number ||
      primaryAccountDetails?.branch?.bank_account?.bank_ifsc !==
        preferredAccount.bank_ifsc
    : primaryAccountDetails?.organisation?.bank_account?.bank_account_number !==
        preferredAccount.bank_account_number ||
      primaryAccountDetails?.organisation?.bank_account?.bank_ifsc !==
        preferredAccount.bank_ifsc;

const isInvalidGSTDetails = (gstInfo: GstInput, formData: BranchForm) => {
  const isComposition = gstInfo.selectedGstType === GstOptions.COMPOSITION;
  return (
    gstInfo.showGstDetails &&
    formData?.country !== 'Others' &&
    (gstInfo.gstIn.trim().length === 0 ||
      !gstinRegex.test(gstInfo.gstIn) ||
      !hsnCodeRegex.test(gstInfo.hsnCode) ||
      gstInfo.hsnCode.trim().length === 0 ||
      (!isComposition &&
        (Number(gstInfo.gstValue.cgst) === 0 ||
          Number(gstInfo.gstValue.sgst) === 0)) ||
      (gstInfo.gstValue.igst && Number(gstInfo.gstValue.igst) === 0))
  );
};

const hasDuplicateFeeCategoryName = (feeList: FeeListType[]) =>
  [...new Set(feeList.map((fee) => fee.category.trim()))].length !==
  feeList.length;

const isFineDetailsValid = (formData: BranchForm) => {
  const fineStartsInDays =
    formData.branchConfig?.fine_config?.fine_starts_in_days ?? 0;
  const fineAmount = formData.branchConfig?.fine_config?.fine_amount ?? 0;

  return Number(fineStartsInDays) > 0 && Number(fineAmount) > 0;
};

export const checkIfSaveDisabled = (
  isOrgLite: boolean,
  uploadImageLoading: boolean,
  onboardBranchLoading: boolean,
  formData: BranchForm,
  branchId: string,
  isFeeCategoryEnabled: boolean,
  feeList: FeeListType[],
  branchDetails: BranchByIdType,
  gstInfo: GstInput,
  accountConnectedWithSelectedSettlementMode: () => boolean,
  isFineEnabled: boolean
) =>
  isOrgLite
    ? isInvalidEntityName(formData?.branchName || '') ||
      uploadImageLoading ||
      onboardBranchLoading
    : isInvalidEntityName(formData?.branchName || '') ||
      uploadImageLoading ||
      onboardBranchLoading ||
      (formData.pincode && formData.pincode.length !== 6) ||
      (!branchId && !accountConnectedWithSelectedSettlementMode()) ||
      (isFeeCategoryEnabled &&
        !feeList.every((fee) => fee.category.trim() !== '')) ||
      (isFeeCategoryEnabled && hasDuplicateFeeCategoryName(feeList)) ||
      (branchDetails.image_url &&
        !(formData.branchImage?.file || formData.branchImage?.image_url)) ||
      isInvalidGSTDetails(gstInfo, formData) ||
      (isFineEnabled && !isFineDetailsValid(formData));

export const getCreateGroupPayload = (
  organizationId: string,
  branchId: string,
  formData: BranchForm,
  branchDetails: BranchByIdType,
  isFeeCategoryEnabled: boolean,
  feeList: FeeListType[],
  preferredAccount: BankAccount,
  gstInfo: GstInput,
  orgConfig: any,
  currentOrg: any,
  isOrgLite: boolean,
  isMultiCurrencySupportEnabled: boolean
) => {
  const gstInfoPayload = {
    activate_gst_feature: gstInfo.showGstDetails,
    gstin: gstInfo.gstIn,
    hsn_code: gstInfo.hsnCode,
    cgst: gstInfo.showGstDetails
      ? gstInfo.gstValue.cgst || null
      : branchDetails?.cgst || null,
    sgst: gstInfo.showGstDetails
      ? gstInfo.gstValue.sgst || null
      : branchDetails?.sgst || null,
    igst: gstInfo.showGstDetails
      ? gstInfo.gstValue.igst || null
      : branchDetails?.igst || null,
    is_inclusive_gst: gstInfo.selectedGstType === GstOptions.INCLUSIVE,
    gst_type: gstInfo.showGstDetails ? gstInfo.selectedGstType : null
  };

  const createBranchFeeConfigFormattedList = feeList.map((item) => ({
    category: item.category.trim(),
    account_detail_id: Number(item.accountId)
  }));

  const updateBranchFeeConfigFormattedList = feeList.map((item) => {
    const exisitingFeeConfig = branchDetails.fee_categories?.find(
      (feeConfig) =>
        feeConfig.id === Number(item.id) &&
        (feeConfig.category !== item.category ||
          feeConfig.account_detail_id !== Number(item.accountId))
    );
    return {
      category: item.category.trim(),
      account_detail_id: Number(item.accountId),
      ...(exisitingFeeConfig ? { id: exisitingFeeConfig.id } : {})
    };
  });

  const fineConfigPayload = {
    fine_amount: formData?.branchConfig?.fine_config?.fine_amount,
    fine_type: formData?.branchConfig?.fine_config?.fine_type ?? 'percentage',
    fine_starts_in_days:
      formData?.branchConfig?.fine_config?.fine_starts_in_days
  };

  const partialPaymentConfigPayload = {
    split_payment_limits: {
      per_split_amount_automatic_min:
        formData?.branchConfig?.limits_config?.split_payment_limits
          ?.per_split_amount_automatic_min ?? null
    }
  };

  const onboardBranchPayload = {
    params: {
      organizationId,
      branchId
    },
    body: {
      name: formData.branchName.trim(),
      address_line_1: formData.addressLine1?.trim() || null,
      address_line_2: formData.addressLine2?.trim() || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      pincode: formData.pincode || null,
      payout_account_type: formData.settlementMode,
      primary_account: preferredAccount.accountDetailId,
      is_fee_configured: isFeeCategoryEnabled,
      ...(orgConfig.is_fee_configuration_enabled && {
        fee_configuration: createBranchFeeConfigFormattedList
      }),
      ...gstInfoPayload,
      is_lite: !!(
        currentOrg?.status === OrgStatus.KYC_PENDING &&
        !currentOrg?.is_kyc_deferred
      ),
      ...(isMultiCurrencySupportEnabled && {
        fine_config: fineConfigPayload,
        limits_config: partialPaymentConfigPayload
      })
    }
  };

  const updateBranchPayload = {
    params: {
      organizationId,
      branchId
    },
    body: {
      name: formData.branchName.trim(),
      address_line_1: formData.addressLine1?.trim() || null,
      address_line_2: formData.addressLine2?.trim() || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      pincode: formData.pincode || null,
      // account_detail_ids: addedAccounts.length
      //   ? addedAccounts.map((item) => item.accountDetailId)
      //   : [],
      primary_account: preferredAccount.accountDetailId,
      ...(isOrgLite === false && {
        is_fee_configured: isFeeCategoryEnabled
      }),
      ...(orgConfig.is_fee_configuration_enabled && {
        fee_configuration: updateBranchFeeConfigFormattedList
      }),
      ...gstInfoPayload,
      ...(isMultiCurrencySupportEnabled && {
        fine_config: fineConfigPayload,
        limits_config: partialPaymentConfigPayload
      })
    }
  };

  return {
    onboardBranchPayload,
    updateBranchPayload,
    updateBranchFeeConfigFormattedList,
    createBranchFeeConfigFormattedList
  };
};

export const getGstInfo = (branchDetails: BranchByIdType) => ({
  address: {
    line1: branchDetails?.address_line_1 || '',
    line2: branchDetails?.address_line_2 || '',
    city: branchDetails?.city || '',
    state: branchDetails?.state || '',
    country: branchDetails?.country || '',
    pincode: branchDetails?.pincode || ''
  },
  gstIn: branchDetails?.gstin || '',
  hsnCode: branchDetails?.hsn_code || '',
  selectedGstType: getBranchEffectiveGstType(branchDetails),
  gstValue: {
    // eslint-disable-next-line no-nested-ternary
    cgst: branchDetails?.cgst
      ? Number(branchDetails?.cgst) > 0
        ? branchDetails?.cgst
        : '9'
      : '',
    // eslint-disable-next-line no-nested-ternary
    sgst: branchDetails?.sgst
      ? Number(branchDetails?.sgst) > 0
        ? branchDetails?.sgst
        : '9'
      : '',
    // eslint-disable-next-line no-nested-ternary
    igst: branchDetails?.igst
      ? Number(branchDetails?.igst) > 0
        ? branchDetails?.igst
        : null
      : null
  },
  showGstDetails: branchDetails?.activate_gst_feature || false
});

// Corresponding field names in api response
export const accountField = {
  [SettlementMode.UPI]: 'upi',
  [SettlementMode.BANK]: 'bank_account'
};
